import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Home, Plus } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

export default function FamiliesDirectory() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canManage = hasRole("super_admin", "direction", "administration", "coordinateur");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/people/families").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  const create = async (v) => { try { await api.post("/people/families", v); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  useEffect(() => {
    load();
  }, []);
  const filtered = rows.filter((f) => !q || (f.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="families-directory">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("familyFiles")}</h1>
        <div className="flex items-center gap-2">
          {canManage && <Button className="gap-2" data-testid="add-family-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />{t("addFamily")}</Button>}
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="family-search" className="pl-9 w-56" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </div>
      <EntityDialog open={open} onOpenChange={setOpen} title={t("addFamily")} submitLabel={t("create")} onSubmit={create}
        fields={[{ name: "name", label: t("name") }, { name: "email", label: t("email") }, { name: "phone", label: t("phone") }, { name: "address", label: t("address") }]} />
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("name")}</TableHead><TableHead>{t("email")}</TableHead><TableHead>{t("phone")}</TableHead>
            <TableHead>{t("children")}</TableHead><TableHead className="text-right">{t("viewFamily")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((f) => (
              <TableRow key={f.id} data-testid="family-dir-row">
                <TableCell className="font-medium text-slate-800">{f.name}</TableCell>
                <TableCell className="text-slate-500">{f.email || "—"}</TableCell>
                <TableCell className="text-slate-500">{f.phone || "—"}</TableCell>
                <TableCell className="text-slate-500">{f.children_count} {t("childrenCount")}</TableCell>
                <TableCell className="text-right">
                  <Link to={`/people/families/${f.id}`} data-testid={`open-family-${f.id}`}>
                    <Button size="sm" variant="outline" className="gap-2"><Home className="h-4 w-4" />{t("viewFamily")}</Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
